import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
} from "react";

import {
  Container,
  Segment,
  Header,
  Icon,
  Grid,
  Table,
  Card,
  Modal,
  Input,
  Label,
  Button,
} from "semantic-ui-react";

import { db } from "../firebase/firebaseConfig";

import {
  collection,
  getDocs,
  where,
  query,
  doc,
  getDoc,
} from "firebase/firestore";

import { UserContext } from "../context/UserContext";

import GraficoTorta from "./GraficoTorta";
import GraficoBarrasApiladas from "./GraficoBarrasApiladas";

import { normalizar } from "../funciones/format";
import { getColorStatus } from "../funciones/colors";

const DashboardEquipos = () => {
  const { users } = useContext(UserContext);

  // ==========================================================
  // DATOS DASHBOARD
  // ==========================================================

  const [estado, setEstado] = useState([]);
  const [datosTorta, setDatosTorta] = useState([]);

  const [tipoVsStatus, setTipoVsStatus] = useState({
    labels: [],
    datasets: [],
  });

  const [equiposPorTipo, setEquiposPorTipo] = useState([]);
  const [pacientesPorTipo, setPacientesPorTipo] = useState([]);

  const [kpis, setKpis] = useState({
    total: 0,
    paciente: 0,
    ocupacion: 0,
    disponibles: 0,
    transito: 0,
    preparacion: 0,
  });

  // ==========================================================
  // CONTRATO MANTENCIÓN
  // ==========================================================

  const [contratoMantencion, setContratoMantencion] =
    useState(null);

  const [loadingContrato, setLoadingContrato] =
    useState(false);

  const [modalContrato, setModalContrato] =
    useState(false);

  const [buscadorContrato, setBuscadorContrato] =
    useState("");

  // ==========================================================
  // STATUS
  // ==========================================================

  const getStatus = async () => {
    if (!users?.emp_id) return;

    try {
      const ref = collection(
        db,
        "status"
      );

      const q = query(
        ref,
        where(
          "emp_id",
          "==",
          users.emp_id
        )
      );

      const snap =
        await getDocs(q);

      setEstado(
        snap.docs.map((documento) => ({
          id: documento.id,
          ...documento.data(),
        }))
      );
    } catch (error) {
      console.error(
        "Error obteniendo status:",
        error
      );

      setEstado([]);
    }
  };

  // ==========================================================
  // CONTRATO
  // ==========================================================

  const getContratoMantencion = async () => {
    if (!users?.emp_id) {
      setContratoMantencion(null);
      return;
    }

    try {
      setLoadingContrato(true);

      /*
       * El contrato se guarda con el emp_id
       * como ID del documento:
       *
       * contratos_mantencion/{emp_id}
       */
      const contratoRef = doc(
        db,
        "contratos_mantencion",
        users.emp_id
      );

      const snap =
        await getDoc(contratoRef);

      if (!snap.exists()) {
        setContratoMantencion(null);
        return;
      }

      setContratoMantencion({
        id: snap.id,
        ...snap.data(),
      });
    } catch (error) {
      console.error(
        "Error obteniendo contrato de mantención:",
        error
      );

      setContratoMantencion(null);
    } finally {
      setLoadingContrato(false);
    }
  };

  // ==========================================================
  // AGRUPAR
  // ==========================================================

  const agruparPorCampo = (
    data,
    campo
  ) => {
    const contador = {};

    data.forEach((item) => {
      const key = normalizar(
        item[campo] ||
          "SIN INFORMACIÓN"
      );

      contador[key] =
        (contador[key] || 0) + 1;
    });

    return Object.entries(contador)
      .map(([label, valor]) => ({
        label,
        valor,
      }))
      .sort(
        (a, b) =>
          b.valor - a.valor
      );
  };

  // ==========================================================
  // TIPO VS STATUS
  // ==========================================================

  const generarTipoVsStatus = (
    data
  ) => {
    const matriz = {};

    data.forEach((item) => {
      const tipo = normalizar(
        item.tipo || "SIN TIPO"
      );

      const status = normalizar(
        item.status ||
          "SIN STATUS"
      );

      if (!matriz[tipo]) {
        matriz[tipo] = {};
      }

      matriz[tipo][status] =
        (matriz[tipo][status] || 0) +
        1;
    });

    const tipos =
      Object.keys(
        matriz
      ).sort();

    const statuses = [
      ...new Set(
        data.map((item) =>
          normalizar(
            item.status ||
              "SIN STATUS"
          )
        )
      ),
    ].sort();

    const datasets =
      statuses.map((status) => ({
        label: status,

        data: tipos.map(
          (tipo) =>
            matriz[tipo][status] ||
            0
        ),

        backgroundColor:
          getColorStatus(status),
      }));

    return {
      labels: tipos,
      datasets,
    };
  };

  // ==========================================================
  // MÉTRICAS
  // ==========================================================

  const calcularMetricas = (
    data
  ) => {
    const total = data.length;

    const paciente =
      data.filter(
        (d) =>
          normalizar(d.status) ===
          "PACIENTE"
      ).length;

    const disponibles =
      data.filter((d) =>
        [
          "BODEGA",
          "DISPONIBLE",
        ].includes(
          normalizar(d.status)
        )
      ).length;

    const transito =
      data.filter((d) =>
        normalizar(
          d.status
        ).includes(
          "TRANSITO"
        )
      ).length;

    const preparacion =
      data.filter(
        (d) =>
          normalizar(d.status) ===
          "PREPARACION"
      ).length;

    const ocupacion =
      total > 0
        ? (
            (paciente /
              total) *
            100
          ).toFixed(1)
        : 0;

    setKpis({
      total,
      paciente,
      ocupacion,
      disponibles,
      transito,
      preparacion,
    });

    setDatosTorta(
      agruparPorCampo(
        data,
        "status"
      )
    );

    setTipoVsStatus(
      generarTipoVsStatus(
        data
      )
    );

    setEquiposPorTipo(
      agruparPorCampo(
        data,
        "tipo"
      )
    );

    setPacientesPorTipo(
      agruparPorCampo(
        data.filter(
          (d) =>
            normalizar(
              d.status
            ) ===
            "PACIENTE"
        ),
        "tipo"
      )
    );
  };

  // ==========================================================
  // CONTRATO ACTIVO
  // ==========================================================

  const mostrarCardContrato =
    useMemo(() => {
      return Boolean(
        contratoMantencion &&
          contratoMantencion.actual ===
            true &&
          contratoMantencion.activo ===
            true &&
          Array.isArray(
            contratoMantencion.tipos_cubiertos
          ) &&
          contratoMantencion
            .tipos_cubiertos.length >
            0
      );
    }, [
      contratoMantencion,
    ]);

  // ==========================================================
  // TIPOS CUBIERTOS
  // ==========================================================

  const tiposCubiertos =
    useMemo(() => {
      if (
        !mostrarCardContrato
      ) {
        return [];
      }

      return [
        ...new Set(
          contratoMantencion.tipos_cubiertos
            .map((tipo) =>
              normalizar(tipo)
            )
            .filter(Boolean)
        ),
      ];
    }, [
      contratoMantencion,
      mostrarCardContrato,
    ]);

  // ==========================================================
  // EQUIPOS CUBIERTOS
  // ==========================================================

  const equiposCubiertos =
    useMemo(() => {
      if (
        !mostrarCardContrato
      ) {
        return [];
      }

      return estado
        .filter((equipo) =>
          tiposCubiertos.includes(
            normalizar(
              equipo.tipo
            )
          )
        )
        .sort((a, b) => {
          const familiaA =
            normalizar(
              a.familia || ""
            );

          const familiaB =
            normalizar(
              b.familia || ""
            );

          if (
            familiaA !==
            familiaB
          ) {
            return familiaA.localeCompare(
              familiaB
            );
          }

          const tipoA =
            normalizar(
              a.tipo || ""
            );

          const tipoB =
            normalizar(
              b.tipo || ""
            );

          if (
            tipoA !== tipoB
          ) {
            return tipoA.localeCompare(
              tipoB
            );
          }

          return String(
            a.serie || ""
          ).localeCompare(
            String(
              b.serie || ""
            )
          );
        });
    }, [
      estado,
      tiposCubiertos,
      mostrarCardContrato,
    ]);

  // ==========================================================
  // EQUIPOS CUBIERTOS FILTRADOS
  // ==========================================================

  const equiposCubiertosFiltrados =
    useMemo(() => {
      const texto =
        buscadorContrato
          .trim()
          .toLowerCase();

      if (!texto) {
        return equiposCubiertos;
      }

      return equiposCubiertos.filter(
        (item) => {
          const campos = [
            item.familia,
            item.tipo,
            item.marca,
            item.modelo,
            item.serie,
            item.status,
            item.n_permanente,
            item.r_permanente,
            item.n_origen,
            item.n_destino,
          ];

          return campos.some(
            (campo) =>
              String(
                campo || ""
              )
                .toLowerCase()
                .includes(
                  texto
                )
          );
        }
      );
    }, [
      equiposCubiertos,
      buscadorContrato,
    ]);

  // ==========================================================
  // STATUS COLOR
  // ==========================================================

  const getStatusStyle = (
    status
  ) => {
    const estadoActual =
      normalizar(
        status || ""
      );

    const color =
      getColorStatus(
        estadoActual
      );

    return {
      background:
        color || "#8995a3",
      color: "#ffffff",
      border: "none",
    };
  };

  // ==========================================================
  // EFECTOS
  // ==========================================================

  useEffect(() => {
    if (!users?.emp_id) {
      return;
    }

    getStatus();
    getContratoMantencion();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users?.emp_id]);

  useEffect(() => {
    calcularMetricas(
      estado
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);


  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        padding:
          "20px 24px 28px",
        background:
          "#f8fafc",
        minHeight:
          "100vh",
        maxWidth: 1500,
        margin: "0 auto",
      }}
    >
      {/* ====================================================== */}
      {/* ENCABEZADO */}
      {/* ====================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          marginBottom: 18,
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Header
            as="h2"
            style={{
              margin: 0,
              color:
                "#16324f",
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Dashboard Ejecutivo de Equipos
          </Header>

          <div
            style={{
              color:
                "#7a8797",
              fontSize: 13,
              marginTop: 5,
            }}
          >
            Estado, ocupación y disponibilidad del parque de equipos clínicos
          </div>
        </div>
      </div>

      {/* ====================================================== */}
      {/* KPI */}
      {/* ====================================================== */}

      <Grid
        columns={
          mostrarCardContrato
            ? 6
            : 5
        }
        stackable
        doubling
        style={{
          marginBottom: 8,
        }}
      >
        <KpiCard
          titulo="TOTAL EQUIPOS"
          valor={kpis.total}
          descripcion="Parque total"
          icon="boxes"
          colorIcono="#0b4f83"
          fondoIcono="#e8f1f8"
        />

        <KpiCard
          titulo="EN PACIENTE"
          valor={kpis.paciente}
          descripcion="Equipos en uso"
          icon="user outline"
          colorIcono="#0bad46"
          fondoIcono="#e8f8ee"
        />

        <KpiCard
          titulo="OCUPACIÓN"
          valor={`${kpis.ocupacion}%`}
          descripcion="% del parque en uso"
          icon="chart pie"
          colorIcono="#18b8bd"
          fondoIcono="#e6f8f9"
        />

        <KpiCard
          titulo="DISPONIBLES"
          valor={
            kpis.disponibles
          }
          descripcion="Listos para uso"
          icon="archive"
          colorIcono="#67839a"
          fondoIcono="#edf2f6"
        />

        <KpiCard
          titulo="EN TRÁNSITO"
          valor={
            kpis.transito
          }
          descripcion="En movimiento"
          icon="truck"
          colorIcono="#ff6d00"
          fondoIcono="#fff0e4"
        />

        {/* ================================================== */}
        {/* SOLO APARECE SI HAY CONTRATO ACTIVO */}
        {/* ================================================== */}

        {mostrarCardContrato && (
          <KpiCard
            titulo="CON CONTRATO"
            valor={
              equiposCubiertos.length
            }
            descripcion={`${tiposCubiertos.length} tipo(s) cubierto(s)`}
            icon="wrench"
            colorIcono="#6435c9"
            fondoIcono="#f1eefd"
            onClick={() => {
              setBuscadorContrato(
                ""
              );

              setModalContrato(
                true
              );
            }}
            clickable
          />
        )}
      </Grid>

      {/* ====================================================== */}
      {/* GRÁFICOS */}
      {/* ====================================================== */}

      <Grid
        stackable
        style={{
          marginTop: 4,
        }}
      >
        <Grid.Column
          width={5}
        >
          <Segment
            style={{
              height: "100%",
              borderRadius: 8,
              border:
                "1px solid #e3e8ee",
              boxShadow:
                "0 2px 7px rgba(0,0,0,0.04)",
              padding: 16,
            }}
          >
            <Header
              as="h4"
              style={{
                color:
                  "#243b53",
                fontSize: 15,
                marginBottom: 12,
              }}
            >
              Estado actual de los equipos
            </Header>

            <div
              style={{
                height: 330,
              }}
            >
              <GraficoTorta
                datos={
                  datosTorta
                }
              />
            </div>
          </Segment>
        </Grid.Column>

        <Grid.Column
          width={11}
        >
          <Segment
            style={{
              height: "100%",
              borderRadius: 8,
              border:
                "1px solid #e3e8ee",
              boxShadow:
                "0 2px 7px rgba(0,0,0,0.04)",
              padding: 16,
            }}
          >
            <Header
              as="h4"
              style={{
                color:
                  "#243b53",
                fontSize: 15,
                marginBottom: 12,
              }}
            >
              Equipos por tipo y estado
            </Header>

            <div
              style={{
                height: 330,
              }}
            >
              <GraficoBarrasApiladas
                datos={
                  tipoVsStatus
                }
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      {/* ====================================================== */}
      {/* TABLAS RESUMEN */}
      {/* ====================================================== */}

      <Grid
        columns={2}
        stackable
        style={{
          marginTop: 2,
        }}
      >
        <Grid.Column>
          <Segment
            style={{
              borderRadius: 8,
              border:
                "1px solid #e3e8ee",
              boxShadow:
                "0 2px 7px rgba(0,0,0,0.04)",
              padding: 0,
              overflow:
                "hidden",
            }}
          >
            <div
              style={{
                padding:
                  "14px 16px",
                borderBottom:
                  "1px solid #edf0f4",
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 8,
                color:
                  "#243b53",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              <Icon name="list ul" />
              Equipos por tipo
            </div>

            <div
              style={{
                padding:
                  "0 12px 8px",
              }}
            >
              <TablaResumen
                datos={
                  equiposPorTipo
                }
                tituloValor="Cantidad"
              />
            </div>
          </Segment>
        </Grid.Column>

        <Grid.Column>
          <Segment
            style={{
              borderRadius: 8,
              border:
                "1px solid #e3e8ee",
              boxShadow:
                "0 2px 7px rgba(0,0,0,0.04)",
              padding: 0,
              overflow:
                "hidden",
            }}
          >
            <div
              style={{
                padding:
                  "14px 16px",
                borderBottom:
                  "1px solid #edf0f4",
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 8,
                color:
                  "#243b53",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              <Icon name="users" />
              Pacientes por tipo de equipo
            </div>

            <div
              style={{
                padding:
                  "0 12px 8px",
              }}
            >
              <TablaResumen
                datos={
                  pacientesPorTipo
                }
                tituloValor="En paciente"
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      {/* ====================================================== */}
      {/* MODAL EQUIPOS CON CONTRATO */}
      {/* ====================================================== */}

      <Modal
        open={
          modalContrato
        }
        onClose={() => {
          setModalContrato(
            false
          );

          setBuscadorContrato(
            ""
          );
        }}
        closeIcon
        size="large"
        style={{
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <Modal.Header
          style={{
            padding:
              "16px 20px",
            borderBottom:
              "1px solid #e5e9ef",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 9,
                background:
                  "#f1eefd",
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
              }}
            >
              <Icon
                name="wrench"
                style={{
                  margin: 0,
                  color:
                    "#6435c9",
                  fontSize: 18,
                }}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color:
                    "#172b4d",
                }}
              >
                Equipos cubiertos por contrato
              </div>

              <div
                style={{
                  marginTop: 3,
                  color:
                    "#8793a5",
                  fontSize: 12,
                  fontWeight: 400,
                }}
              >
                Equipos cuyo tipo forma parte del contrato de mantención
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Content
          scrolling
          style={{
            padding: 0,
          }}
        >
          {/* ================================================ */}
          {/* RESUMEN */}
          {/* ================================================ */}

          <div
            style={{
              padding:
                "14px 18px",
              borderBottom:
                "1px solid #edf0f4",
              background:
                "#ffffff",
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Label
                basic
                color="green"
              >
                <Icon name="check circle" />
                Contrato activo
              </Label>

              <Label
                basic
                color="violet"
              >
                {
                  equiposCubiertos.length
                }{" "}
                equipos
              </Label>

              <Label
                basic
                color="teal"
              >
                {
                  tiposCubiertos.length
                }{" "}
                tipos cubiertos
              </Label>
            </div>

            <div
              style={{
                width: 330,
                maxWidth:
                  "100%",
              }}
            >
              <Input
                fluid
                icon="search"
                iconPosition="left"
                placeholder="Buscar serie, tipo, paciente..."
                value={
                  buscadorContrato
                }
                onChange={(e) =>
                  setBuscadorContrato(
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* ================================================ */}
          {/* TIPOS DEL CONTRATO */}
          {/* ================================================ */}

          <div
            style={{
              padding:
                "12px 18px",
              background:
                "#fafbfc",
              borderBottom:
                "1px solid #edf0f4",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color:
                  "#697586",
                fontWeight: 600,
                marginBottom: 7,
              }}
            >
              TIPOS CUBIERTOS
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {tiposCubiertos.map(
                (tipo) => (
                  <Label
                    key={tipo}
                    basic
                    color="teal"
                    size="small"
                  >
                    {tipo}
                  </Label>
                )
              )}
            </div>
          </div>

          {/* ================================================ */}
          {/* TABLA */}
          {/* ================================================ */}

          <div
            style={{
              overflowX:
                "auto",
              maxHeight: 500,
            }}
          >
            <Table
              celled
              compact
              selectable
              unstackable
              style={{
                margin: 0,
                border: 0,
                minWidth: 1050,
                fontSize: 12,
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    textAlign="center"
                    style={
                      stickyTh
                    }
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Familia
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Tipo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Marca / Modelo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Serie
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Estado
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Paciente
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {equiposCubiertosFiltrados.map(
                  (
                    item,
                    index
                  ) => (
                    <Table.Row
                      key={
                        item.id
                      }
                    >
                      <Table.Cell
                        textAlign="center"
                      >
                        {index +
                          1}
                      </Table.Cell>

                      <Table.Cell>
                        <strong
                          style={{
                            color:
                              "#344563",
                          }}
                        >
                          {item.familia ||
                            "-"}
                        </strong>
                      </Table.Cell>

                      <Table.Cell>
                        {item.tipo ||
                          "-"}
                      </Table.Cell>

                      <Table.Cell>
                        <div>
                          {item.marca ||
                            "-"}
                        </div>

                        <div
                          style={{
                            marginTop: 2,
                            fontSize: 11,
                            color:
                              "#8793a5",
                          }}
                        >
                          {item.modelo ||
                            "-"}
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <span
                          style={{
                            fontFamily:
                              "monospace",
                            fontWeight:
                              600,
                            color:
                              "#344563",
                          }}
                        >
                          {item.serie ||
                            "-"}
                        </span>
                      </Table.Cell>

                      <Table.Cell>
                        <Label
                          size="tiny"
                          style={{
                            ...getStatusStyle(
                              item.status
                            ),
                            margin: 0,
                          }}
                        >
                          {normalizar(
                            item.status ||
                              "SIN STATUS"
                          )}
                        </Label>
                      </Table.Cell>

                      <Table.Cell>
                        {item.n_permanente ? (
                          <>
                            <div
                              style={{
                                fontWeight:
                                  600,
                                color:
                                  "#344563",
                              }}
                            >
                              {
                                item.n_permanente
                              }
                            </div>

                            {item.r_permanente && (
                              <div
                                style={{
                                  color:
                                    "#8793a5",
                                  fontSize:
                                    11,
                                  marginTop:
                                    2,
                                }}
                              >
                                {
                                  item.r_permanente
                                }
                              </div>
                            )}
                          </>
                        ) : (
                          <span
                            style={{
                              color:
                                "#98a1af",
                            }}
                          >
                            Sin paciente
                          </span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}

                {equiposCubiertosFiltrados.length ===
                  0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={7}
                      textAlign="center"
                      style={{
                        padding: 35,
                        color:
                          "#8995a3",
                      }}
                    >
                      <Icon name="search" />

                      No se encontraron equipos
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </Modal.Content>

        <Modal.Actions
          style={{
            background:
              "#f8fafc",
            borderTop:
              "1px solid #e5e9ef",
          }}
        >
          <Button
            type="button"
            onClick={() => {
              setModalContrato(
                false
              );

              setBuscadorContrato(
                ""
              );
            }}
          >
            Cerrar
          </Button>
        </Modal.Actions>
      </Modal>

      {loadingContrato && null}
    </Container>
  );
};

// ============================================================
// KPI CARD
// ============================================================

const KpiCard = ({
  titulo,
  valor,
  descripcion,
  icon,
  colorIcono,
  fondoIcono,
  onClick,
  clickable = false,
}) => (
  <Grid.Column>
    <Card
      fluid
      onClick={
        clickable
          ? onClick
          : undefined
      }
      style={{
        borderRadius: 8,
        border:
          "1px solid #e3e8ee",
        boxShadow:
          "0 2px 7px rgba(0,0,0,0.04)",
        minHeight: 105,
        margin: 0,
        cursor:
          clickable
            ? "pointer"
            : "default",
        transition:
          "box-shadow 0.15s ease, transform 0.15s ease",
      }}
    >
      <Card.Content
        style={{
          display: "flex",
          alignItems: "center",
          padding:
            "17px 16px",
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            minWidth: 52,
            borderRadius: 14,
            background:
              fondoIcono,
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            marginRight: 16,
          }}
        >
          <Icon
            name={icon}
            style={{
              margin: 0,
              fontSize: 22,
              color:
                colorIcono,
            }}
          />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color:
                "#52667a",
              fontWeight: 600,
              letterSpacing: 0.2,
              marginBottom: 2,
            }}
          >
            {titulo}
          </div>

          <div
            style={{
              fontSize: 30,
              lineHeight: 1.05,
              fontWeight: 700,
              color:
                colorIcono,
            }}
          >
            {valor}
          </div>

          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              marginTop: 7,
              color:
                clickable
                  ? colorIcono
                  : "#7c8897",
              fontSize: 10,
              fontWeight:
                clickable
                  ? 600
                  : 400,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius:
                  "50%",
                background:
                  colorIcono,
                display:
                  "inline-block",
                marginRight: 6,
              }}
            />

            {descripcion}

            {clickable && (
              <Icon
                name="angle right"
                style={{
                  marginLeft: 4,
                  marginRight: 0,
                }}
              />
            )}
          </div>
        </div>
      </Card.Content>
    </Card>
  </Grid.Column>
);

// ============================================================
// TABLA RESUMEN
// ============================================================

const TablaResumen = ({
  datos,
  tituloValor,
}) => (
  <Table
    basic="very"
    compact
    style={{
      margin: 0,
      border: 0,
      fontSize: 12,
    }}
  >
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell
          style={{
            color:
              "#34495e",
            fontWeight: 600,
            background:
              "#fafbfc",
          }}
        >
          Tipo
        </Table.HeaderCell>

        <Table.HeaderCell
          textAlign="right"
          style={{
            color:
              "#34495e",
            fontWeight: 600,
            background:
              "#fafbfc",
          }}
        >
          {tituloValor}
        </Table.HeaderCell>
      </Table.Row>
    </Table.Header>

    <Table.Body>
      {datos.map(
        (item) => (
          <Table.Row
            key={
              item.label
            }
          >
            <Table.Cell
              style={{
                color:
                  "#536273",
              }}
            >
              {item.label}
            </Table.Cell>

            <Table.Cell
              textAlign="right"
              style={{
                color:
                  "#36485b",
                fontWeight:
                  500,
              }}
            >
              {item.valor}
            </Table.Cell>
          </Table.Row>
        )
      )}

      {datos.length ===
        0 && (
        <Table.Row>
          <Table.Cell
            colSpan="2"
            textAlign="center"
            style={{
              color:
                "#8995a3",
              padding: 20,
            }}
          >
            Sin datos
          </Table.Cell>
        </Table.Row>
      )}
    </Table.Body>
  </Table>
);

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#344563",
  fontWeight: 600,
};

export default DashboardEquipos;